import { captureRetainedAudioOwner, readRetainedAudio, retainedAudioOwnerIsCurrent } from "./retained-audio-store";
import { bindRetainedAudioPauseCheckpoint, readRetainedAudioCheckpoint } from "./retained-audio-resume";
import { retainedAudioCheckpointMatchesRecording } from "./retained-audio-resume-policy.mjs";
import { bufferedFraction, finiteDuration } from "./audio-media-utils.mjs";
import { defaultDownloadName } from "./audio-download-name.mjs";
import { publicWebCopyForLang } from "@/shared/i18n/public-web-copy";
import type { AudioPlaybackState } from "./audio-playback-state";

type Runtime = {
  beginRequest: () => { isCurrent: () => boolean; finish: () => void };
  createPlayer: () => HTMLAudioElement;
  isPlayerCurrent: (player: HTMLAudioElement) => boolean;
  clearPlayer: (player: HTMLAudioElement, objectUrl: string | null, preserve?: boolean) => void;
  attachEvents: (player: HTMLAudioElement, opts: { key: string; label: string; lang?: string }) => { armContent: (url: string) => void };
  replaceObjectUrl: (url: string) => void;
  setMediaSession: (label: string, detail: string, language: string) => void;
  setState: (state: AudioPlaybackState) => void;
  getState: () => AudioPlaybackState;
  getRate: () => number;
  clearEditionContext: () => void;
};
let runtime: Runtime | null = null;
export function installRetainedAudioRuntime(value: Runtime) { runtime = value; }
function loadedMetadata(player: HTMLAudioElement, sourceUrl: string): Promise<void> {
  const readyForSource = () => player.currentSrc === sourceUrl && player.readyState >= 1 && Number.isFinite(player.duration) && player.duration > 0;
  if (readyForSource()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const clear = () => { window.clearTimeout(timer); player.removeEventListener("loadedmetadata", ready); player.removeEventListener("error", failed); };
    const ready = () => { if (readyForSource()) { clear(); resolve(); } };
    const failed = () => { clear(); reject(new Error("audio_recording_unavailable")); };
    const timer = window.setTimeout(failed, 10_000);
    player.addEventListener("loadedmetadata", ready); player.addEventListener("error", failed, { once: true });
  });
}
/** Resolve retained bytes directly. Replay has no network, narration, entitlement
 * reservation, completion receipt, or retention write. Resume is explicit. */
export function playRetainedAudio(id: string, options: { resume?: boolean } = {}): Promise<AudioPlaybackState> {
  return playRecording(id, options);
}
/** Recap clips use only a measured section interval of the exact full recording. */
export function playRetainedBriefRecapSection(id: string, sectionId: string): Promise<AudioPlaybackState> {
  return playRecording(id, { sectionId });
}
async function playRecording(id: string, options: { resume?: boolean; sectionId?: string }): Promise<AudioPlaybackState> {
  if (!runtime) throw new Error("audio_player_unavailable");
  const playerRuntime = runtime;
  const owner = captureRetainedAudioOwner();
  // Snapshot before creating a new player invalidates the previous pause offer.
  const checkpoint = options.resume ? readRetainedAudioCheckpoint(owner) : null;
  const request = playerRuntime.beginRequest();
  playerRuntime.clearEditionContext();
  const player = playerRuntime.createPlayer();
  const key = options.sectionId ? `retained-recap:${id}:${options.sectionId}` : `retained-brief:${id}`;
  let objectUrl: string | null = null;
  let label = "";
  let language = "en";
  let removeClipEvents = () => {};
  const current = () => request.isCurrent() && retainedAudioOwnerIsCurrent(owner);
  playerRuntime.setState({ phase: "loading", key });
  try {
    const recording = await readRetainedAudio(id, owner);
    if (!current()) { playerRuntime.clearPlayer(player, objectUrl, true); return playerRuntime.getState(); }
    if (!recording) throw new Error("audio_recording_unavailable");
    label = recording.label; language = recording.language;
    const digest = await crypto.subtle.digest("SHA-256", await recording.blob.arrayBuffer());
    const hash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
    if (!current()) { playerRuntime.clearPlayer(player, objectUrl, true); return playerRuntime.getState(); }
    if (hash !== recording.id || recording.expiresAtEpochMs <= Date.now()) throw new Error("audio_recording_unavailable");
    const section = options.sectionId ? recording.sections.find((row) => row.id === options.sectionId) : null;
    if (options.sectionId && (recording.kind !== "brief" || !section)) throw new Error("audio_recording_unavailable");
    if (options.resume && !retainedAudioCheckpointMatchesRecording(checkpoint, recording, owner.scope)) throw new Error("audio_recording_unavailable");
    const startMs = section?.startMs || (options.resume ? checkpoint!.positionMs : 0);
    const endMs = section?.endMs;
    if (section) label = section.title || recording.label;
    objectUrl = URL.createObjectURL(recording.blob);
    playerRuntime.replaceObjectUrl(objectUrl);
    const events = playerRuntime.attachEvents(player, { key, label, lang: language });
    player.loop = false; player.src = objectUrl; events.armContent(objectUrl); player.load();
    player.playbackRate = playerRuntime.getRate();
    if (options.resume || section) {
      await loadedMetadata(player, objectUrl);
      if (!current()) { playerRuntime.clearPlayer(player, objectUrl, true); return playerRuntime.getState(); }
      const durationMs = Math.round(finiteDuration(player) * 1000);
      if (startMs < 0 || startMs >= durationMs || Math.abs(recording.durationMs - durationMs) > 1000 ||
        (endMs !== undefined && (endMs <= startMs || endMs > durationMs))) throw new Error("audio_recording_unavailable");
      player.currentTime = startMs / 1000;
    }
    if (endMs !== undefined) {
      const boundary = () => {
        if (!playerRuntime.isPlayerCurrent(player)) { removeClipEvents(); return; }
        if (player.currentTime * 1000 < startMs) player.currentTime = startMs / 1000;
        if (player.currentTime * 1000 >= endMs) { player.pause(); if (player.currentTime * 1000 > endMs) player.currentTime = endMs / 1000; }
      };
      removeClipEvents = () => { player.removeEventListener("timeupdate", boundary); player.removeEventListener("ended", boundary); player.removeEventListener("seeking", boundary); player.removeEventListener("play", boundary); };
      player.addEventListener("timeupdate", boundary); player.addEventListener("ended", boundary); player.addEventListener("seeking", boundary); player.addEventListener("play", boundary);
    }
    await player.play();
    if (!current()) { removeClipEvents(); playerRuntime.clearPlayer(player, objectUrl, true); return playerRuntime.getState(); }
    playerRuntime.setMediaSession(label, recording.detail, language);
    if (!section) bindRetainedAudioPauseCheckpoint({ owner, recording, player, isPlayerCurrent: () => playerRuntime.isPlayerCurrent(player) });
    const state: AudioPlaybackState = {
      phase: "playing", key, label, detail: recording.detail, paused: false, buffering: false,
      positionMs: startMs, durationMs: Math.round(finiteDuration(player) * 1000),
      buffered01: bufferedFraction(player), playbackRate: playerRuntime.getRate(), cache: "retained",
      canDownload: !section, downloadName: section ? undefined : defaultDownloadName(key, label),
    };
    playerRuntime.setState(state); request.finish();
    return state;
  } catch {
    removeClipEvents();
    const stillCurrent = current();
    playerRuntime.clearPlayer(player, objectUrl, !stillCurrent);
    if (!stillCurrent) return playerRuntime.getState();
    request.finish();
    const state: AudioPlaybackState = { phase: "error", key, label, code: "audio_recording_unavailable", detail: publicWebCopyForLang(language)("saved.audioUnavailable") };
    playerRuntime.setState(state);
    return state;
  }
}
